/**
 * /assistant and /assistant/$id — Fusion AI full page (Hermes' features): conversations grouped by project, search,
 * projects with a persona, Fusion AI's own briefs, and the conversation with Minutes, rename, project picker and delete.
 * Standing instructions, proactive messages and memory live on /assistant/settings.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import clsx from "clsx";
import { Check, ChevronDown, ChevronRight, FolderPlus, ListChecks, MessageSquare, Pencil, Plus, Search, Settings2, Sparkles, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, post } from "../lib/api";
import { BriefMenu } from "../components/assistant/BriefMenu";
import { isBrief, saveOpenId, sortRecent, AK, call, errText, type AsConversation, type AsProject, type AsSearchHit } from "../components/assistant/api";
import { Conversation, useSeconds, useThread } from "../components/assistant/Chat";
import { useAssistantState } from "../components/assistant/FusionAi";
import { ErrorNote } from "../components/ui";

const sideLabel = "section-label mb-1 mt-3 flex items-center gap-1 px-1";
const shortDay = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

export function AssistantPage() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const state = useAssistantState();
  const [showSide, setShowSide] = useState(false);
  const [draftProject, setDraftProject] = useState<string | null>(null);

  const convId = id ?? null;
  useEffect(() => { if (convId) saveOpenId(convId); }, [convId]);

  const go = useCallback((cid: string | null) => {
    setShowSide(false);
    if (cid) void navigate({ to: "/assistant/$id", params: { id: cid } });
    else void navigate({ to: "/assistant" });
  }, [navigate]);
  const refresh = () => qc.invalidateQueries({ queryKey: AK.state });

  const startIn = async (projectId: string | null) => {
    if (!projectId) { setDraftProject(null); go(null); return; }
    const r = await call<{ conversation: AsConversation }>("POST", "/api/assistant/conversations", { projectId });
    if (!r.ok || !r.body?.conversation) { window.alert(errText(r.body, r.status)); return; }
    await refresh();
    go(r.body.conversation.id);
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-[270px_minmax(0,1fr)]">
      <button type="button" className="btn btn-sm justify-self-start md:hidden" aria-expanded={showSide} onClick={() => setShowSide((s) => !s)}>
        <MessageSquare size={14} /> Conversations, projects and settings {showSide ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      <aside className={clsx("panel scroll-thin min-h-0 flex-col p-3 md:flex md:h-[calc(100dvh-8.5rem)] md:overflow-y-auto", showSide ? "flex" : "hidden")}>
        <Side
          convId={convId}
          conversations={state.data?.conversations ?? []}
          projects={state.data?.projects ?? []}
          model={state.data?.model}
          error={state.error}
          onOpen={go}
          onNew={(pid) => void startIn(pid)}
          onChanged={() => void refresh()}
        />
      </aside>
      <section className="panel flex h-[calc(100dvh-13rem)] min-h-[420px] min-w-0 flex-col p-3 md:h-[calc(100dvh-8.5rem)]">
        <Main
          convId={convId}
          projects={state.data?.projects ?? []}
          draftProject={draftProject}
          setDraftProject={setDraftProject}
          onOpen={go}
          onBrief={(cid) => { void refresh(); go(cid); }}
          onChanged={() => void refresh()}
        />
      </section>
    </div>
  );
}

function Main({ convId, projects, draftProject, setDraftProject, onOpen, onBrief, onChanged }: {
  convId: string | null; projects: AsProject[]; draftProject: string | null; setDraftProject: (p: string | null) => void;
  onOpen: (id: string | null) => void; onBrief: (id: string) => void; onChanged: () => void;
}) {
  const qc = useQueryClient();
  const thread = useThread(convId);
  const conv = thread.data?.conversation;
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [minutes, setMinutes] = useState(false);
  const [err, setErr] = useState("");
  const seconds = useSeconds(minutes);
  const hasUserText = (thread.data?.messages ?? []).some((m) => m.role === "user");
  useEffect(() => { setEditing(false); setErr(""); }, [convId]);

  const patchConv = async (body: { title?: string; projectId?: string | null }) => {
    if (!convId) return;
    setErr("");
    const r = await call("PATCH", `/api/assistant/conversations/${convId}`, body);
    if (!r.ok) { setErr(errText(r.body, r.status)); return; }
    await qc.invalidateQueries({ queryKey: AK.thread(convId) });
    onChanged();
  };
  const saveTitle = async () => {
    const t = title.trim();
    setEditing(false);
    if (t && t !== conv?.title) await patchConv({ title: t });
  };
  const remove = async () => {
    if (!convId || !window.confirm(`Delete "${conv?.title || "this conversation"}"? This cannot be undone.`)) return;
    const r = await call("DELETE", `/api/assistant/conversations/${convId}`, {});
    if (!r.ok) { setErr(errText(r.body, r.status)); return; }
    saveOpenId(null);
    onChanged();
    onOpen(null);
  };
  const makeMinutes = async () => {
    if (!convId) return;
    setMinutes(true); setErr("");
    try {
      const r = await call("POST", `/api/assistant/conversations/${convId}/minutes`, {});
      if (!r.ok) setErr(errText(r.body, r.status));
    } catch (e) { setErr((e as Error).message); }
    await qc.invalidateQueries({ queryKey: AK.thread(convId) });
    setMinutes(false);
  };

  const projectValue = convId ? conv?.projectId ?? "" : draftProject ?? "";
  // ONE projects (SICCA …) the person can see; picking one links the conversation to it (a Fusion AI folder made on first use)
  const oneProjects = useQuery({ queryKey: ["projects", "for-assistant"], queryFn: () => api<{ items: Array<{ id: string; ref: string; name: string; status: string }> }>("/api/projects"), staleTime: 300_000 });
  const linked = new Map(projects.filter((p) => p.oneProjectId).map((p) => [p.oneProjectId!, p.id]));
  const pickProject = async (v: string) => {
    let id: string | null = v || null;
    if (v.startsWith("one:")) id = linked.get(v.slice(4)) ?? (await post<{ id: string }>(`/api/assistant/projects/one/${v.slice(4)}`, {})).id;
    if (convId) await patchConv({ projectId: id }); else setDraftProject(id);
    void qc.invalidateQueries({ queryKey: AK.state });
  };
  return (
    <>
      <header className="mb-2 flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--line)] pb-2.5">
        <Sparkles size={17} className="shrink-0 text-brand-600" />
        {editing ? (
          <form className="flex min-w-0 flex-1 items-center gap-1" onSubmit={(e) => { e.preventDefault(); void saveTitle(); }}>
            <input className="input !min-h-0 min-w-0 flex-1 py-1.5 text-sm" aria-label="Conversation title" maxLength={120} value={title} autoFocus onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }} />
            <button type="submit" className="btn btn-sm" aria-label="Save title"><Check size={13} /></button>
            <button type="button" className="btn btn-ghost btn-sm" aria-label="Cancel rename" onClick={() => setEditing(false)}><X size={13} /></button>
          </form>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <h1 className="truncate text-[1.05rem] font-bold">{convId ? conv?.title || "Conversation" : "New chat"}</h1>
            {convId && conv && <button type="button" className="btn btn-ghost btn-icon !h-7 !min-h-0 !w-7 shrink-0" aria-label="Rename conversation" title="Rename" onClick={() => { setTitle(conv.title); setEditing(true); }}><Pencil size={13} /></button>}
          </div>
        )}
        <BriefMenu align="left" onOpen={onBrief} />
        <select
          aria-label="Project"
          className="input !min-h-0 w-auto max-w-[11rem] py-1.5 text-sm"
          value={projectValue}
          onChange={(e) => void pickProject(e.target.value)}
        >
          <option value="">No project</option>
          {!!oneProjects.data?.items.length && (
            <optgroup label="ONE projects">
              {oneProjects.data.items.map((op) => <option key={op.id} value={linked.get(op.id) ?? `one:${op.id}`}>{op.ref ? `${op.ref} · ${op.name}` : op.name}</option>)}
            </optgroup>
          )}
          {projects.some((p) => !p.oneProjectId) && (
            <optgroup label="Fusion AI folders">
              {projects.filter((p) => !p.oneProjectId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
          )}
        </select>
        {convId && (
          <>
            <button type="button" className="btn btn-sm" disabled={minutes || !hasUserText} title={hasUserText ? "Turn what you typed in this conversation into meeting minutes, with task and note cards" : "Type your meeting notes here first"} onClick={() => void makeMinutes()}>
              <ListChecks size={14} /> Minutes
            </button>
            <button type="button" className="btn btn-ghost btn-icon btn-sm !w-8 text-red-600" aria-label="Delete conversation" title="Delete conversation" onClick={() => void remove()}><Trash2 size={14} /></button>
          </>
        )}
      </header>
      {err && <p role="alert" className="mb-1 text-sm text-red-600">{err}</p>}
      <div className="min-h-0 flex-1">
        <Conversation
          id={convId}
          projectId={draftProject}
          onCreated={(cid) => { onChanged(); onOpen(cid); }}
          onMissing={() => onOpen(null)}
          busyLabel={minutes ? "Writing the minutes…" : null}
          busySeconds={seconds}
        />
      </div>
    </>
  );
}

const BRIEFS_SHOWN = 7;

function Side({ convId, conversations, projects, model, error, onOpen, onNew, onChanged }: {
  convId: string | null; conversations: AsConversation[]; projects: AsProject[]; model?: string; error: unknown;
  onOpen: (id: string | null) => void; onNew: (projectId: string | null) => void; onChanged: () => void;
}) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const search = useQuery({
    queryKey: AK.search(debounced),
    queryFn: () => api<{ results: AsSearchHit[] }>(`/api/assistant/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2,
  });

  const [allBriefs, setAllBriefs] = useState(false);
  const briefs = useMemo(() => [...conversations].filter(isBrief).sort((a, b) => (b.day ?? b.createdAt).localeCompare(a.day ?? a.createdAt) || b.createdAt.localeCompare(a.createdAt)), [conversations]);
  const groups = useMemo(() => {
    const sorted = sortRecent(conversations.filter((c) => !isBrief(c)));
    const out: Array<{ key: string; name: string; project: AsProject | null; items: AsConversation[] }> = projects.map((p) => ({ key: p.id, name: p.name, project: p, items: sorted.filter((c) => c.projectId === p.id) }));
    const known = new Set(projects.map((p) => p.id));
    out.push({ key: "none", name: "No project", project: null, items: sorted.filter((c) => !c.projectId || !known.has(c.projectId)) });
    return out;
  }, [conversations, projects]);

  const row = (c: AsConversation) => (
    <li key={c.id}>
      <button
        type="button"
        onClick={() => onOpen(c.id)}
        aria-current={c.id === convId ? "page" : undefined}
        className={clsx("flex w-full items-baseline gap-2 rounded-md px-2 py-1.5 text-left text-[0.8125rem]", c.id === convId ? "bg-brand-50 font-semibold text-brand-700" : "text-[var(--text-2)] hover:bg-[var(--panel-2)]")}
      >
        <span className="min-w-0 flex-1 truncate">{c.title || "Untitled"}</span>
        <span className="subtle shrink-0 text-[11px]">{shortDay(c.updatedAt)}</span>
      </button>
    </li>
  );

  return (
    <>
      <button type="button" className="btn btn-primary btn-sm" onClick={() => onNew(null)}><Plus size={14} /> New chat</button>
      <label className="relative mt-2 block">
        <Search size={14} className="subtle pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input className="input !min-h-0 py-1.5 pl-8 text-sm" type="search" placeholder="Search conversations" aria-label="Search conversations" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <ErrorNote error={error} />

      {debounced.length >= 2 ? (
        <div className="mt-2">
          <div className={sideLabel}>Results</div>
          {search.isLoading && <p className="muted px-1 text-xs">Searching…</p>}
          <ErrorNote error={search.error} />
          {search.data && !search.data.results.length && <p className="muted px-1 text-xs">Nothing found.</p>}
          <ul className="space-y-0.5">
            {(search.data?.results ?? []).map((h) => (
              <li key={h.messageId}>
                <button type="button" className="w-full rounded-md px-2 py-1.5 text-left hover:bg-[var(--panel-2)]" onClick={() => { setQ(""); onOpen(h.conversationId); }}>
                  <span className="block truncate text-[0.8125rem] font-semibold">{h.title || "Untitled"}</span>
                  <span className="muted line-clamp-2 block text-xs">{h.role === "user" ? "You: " : ""}{h.snippet}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-1">
          {briefs.length > 0 && (
            <div>
              <div className={sideLabel}><Sparkles size={12} /> <span className="min-w-0 flex-1 truncate">From Fusion AI</span></div>
              <ul className="space-y-px">{(allBriefs ? briefs : briefs.slice(0, BRIEFS_SHOWN)).map(row)}</ul>
              {briefs.length > BRIEFS_SHOWN && (
                <button type="button" className="subtle px-2 py-1 text-xs hover:underline" onClick={() => setAllBriefs((v) => !v)}>
                  {allBriefs ? "Show fewer" : `Show ${briefs.length - BRIEFS_SHOWN} more`}
                </button>
              )}
            </div>
          )}
          {groups.filter((g) => g.items.length || g.project).map((g) => (
            <div key={g.key}>
              <div className={sideLabel}>
                <span className="min-w-0 flex-1 truncate">{g.name}</span>
                {g.project && <button type="button" className="rounded p-0.5 hover:bg-[var(--panel-2)]" aria-label={`New chat in ${g.name}`} title={`New chat in ${g.name}`} onClick={() => onNew(g.project!.id)}><Plus size={12} /></button>}
              </div>
              {g.items.length ? <ul className="space-y-px">{g.items.map(row)}</ul> : <p className="subtle px-2 text-xs">No conversations yet.</p>}
            </div>
          ))}
          {!conversations.length && <p className="muted px-1 pt-2 text-xs">No conversations yet. Ask anything to start one.</p>}
        </div>
      )}

      <Projects projects={projects} onChanged={onChanged} />
      <Link to="/assistant/settings" className="mt-3 flex items-center gap-1.5 rounded-md border-t border-[var(--line)] px-1 pt-3 text-[0.8125rem] text-[var(--text-2)] hover:text-brand-600">
        <Settings2 size={14} /> Fusion AI settings
      </Link>
      {model && <p className="subtle mt-3 flex items-center gap-1 px-1 text-[11px]"><Sparkles size={11} /> {model}</p>}
    </>
  );
}

function Projects({ projects, onChanged }: { projects: AsProject[]; onChanged: () => void }) {
  const [name, setName] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setErr("");
    const r = await call("POST", "/api/assistant/projects", { name: n });
    if (!r.ok) { setErr(errText(r.body, r.status)); return; }
    setName("");
    onChanged();
  };
  return (
    <div className="mt-3 border-t border-[var(--line)] pt-1">
      <div className={sideLabel}><FolderPlus size={12} /> Projects</div>
      <ul className="space-y-0.5">
        {projects.map((p) => (
          <li key={p.id}>
            {editId === p.id ? (
              <ProjectEditor p={p} onClose={() => setEditId(null)} onChanged={onChanged} />
            ) : (
              <div className="flex items-center gap-1 rounded-md px-2 py-1 text-[0.8125rem] hover:bg-[var(--panel-2)]">
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                {p.persona?.name && <span className="subtle truncate text-[11px]">as {p.persona.name}</span>}
                <button type="button" className="rounded p-1 hover:bg-[var(--panel)]" aria-label={`Edit project ${p.name}`} title="Edit project" onClick={() => setEditId(p.id)}><Pencil size={12} /></button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <form className="mt-1.5 flex gap-1" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        <input className="input !min-h-0 min-w-0 flex-1 py-1.5 text-sm" placeholder="New project" aria-label="New project name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
        <button type="submit" className="btn btn-sm" disabled={!name.trim()} aria-label="Create project"><Plus size={13} /></button>
      </form>
      {err && <p role="alert" className="mt-1 text-xs text-red-600">{err}</p>}
    </div>
  );
}

function ProjectEditor({ p, onClose, onChanged }: { p: AsProject; onClose: () => void; onChanged: () => void }) {
  const [name, setName] = useState(p.name);
  const [persona, setPersona] = useState(p.persona?.name ?? "");
  const [instructions, setInstructions] = useState(p.persona?.instructions ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setBusy(true); setErr("");
    const hasPersona = persona.trim() || instructions.trim();
    const r = await call("PATCH", `/api/assistant/projects/${p.id}`, { name: name.trim() || p.name, persona: hasPersona ? { name: persona.trim(), instructions: instructions.trim() } : null });
    setBusy(false);
    if (!r.ok) { setErr(errText(r.body, r.status)); return; }
    onChanged();
    onClose();
  };
  const remove = async () => {
    if (!window.confirm(`Delete the project "${p.name}"? Its conversations stay, under No project.`)) return;
    const r = await call("DELETE", `/api/assistant/projects/${p.id}`, {});
    if (!r.ok) { setErr(errText(r.body, r.status)); return; }
    onChanged();
    onClose();
  };
  return (
    <div className="space-y-1.5 rounded-lg border border-[var(--line)] bg-[var(--panel-2)] p-2 text-xs">
      <label className="block"><span className="muted mb-0.5 block">Name</span><input className="input !min-h-0 py-1 text-sm" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="block"><span className="muted mb-0.5 block">Persona name (optional)</span><input className="input !min-h-0 py-1 text-sm" placeholder="e.g. Board secretary" maxLength={60} value={persona} onChange={(e) => setPersona(e.target.value)} /></label>
      <label className="block"><span className="muted mb-0.5 block">Persona instructions</span><textarea className="input min-h-20 py-1 text-sm" placeholder="How Fusion AI should behave in this project" maxLength={4000} value={instructions} onChange={(e) => setInstructions(e.target.value)} /></label>
      {err && <p role="alert" className="text-red-600">{err}</p>}
      <div className="flex items-center gap-1.5">
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void save()}>Save</button>
        <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-ghost btn-sm ml-auto text-red-600" aria-label={`Delete project ${p.name}`} onClick={() => void remove()}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}
